/* eslint-disable no-prototype-builtins */
/* eslint-disable react/no-unescaped-entities */

import React from 'react'
import axios from 'axios'

// mui
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CardMedia from '@mui/material/CardMedia'
import Slider from '@mui/material/Slider'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import CheckIcon from '@mui/icons-material/Check'
import AddIcon from '@mui/icons-material/Add'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ReplayIcon from '@mui/icons-material/Replay'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'


import { agesArray, heightInchesArray, heightCMsArray, promptsArray, positionChangeWidthSm } from './variableDefaults'
import { standardToggle } from './toggles'
import { inchesToFeetString, dummyBoxWidth } from './globalHelpers'
import { vitalInfoTextField, descriptionTextField } from './textfields'
import { selectElement, selectElementWithIndex } from './selects'
import { standardButton } from './buttons'

// ? Common Elements

export const titleAndAdvisoryJSX = (title, advisory, marginTop) => {
  return (
    <Box
      sx={{
        mt: marginTop, mb: 1,
        width: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          pt: 0, pb: 0,
          pl: 0,
          fontSize: '20px',
          fontWeight: 'bold',
        }}
      >
        {title}
      </Typography>

      {/* Advisory */}
      <Typography 
        component='pre'
        textAlign={'left'}
        sx={{
          mt: -0.5,
          width: '85%', maxWidth: '275px',
          fontStyle: 'italic',
          color: 'blue',
        }}
      >
        {`${advisory}`.replace(/ {5} +/g, '')}
      </Typography>
    </Box>
  )
}

export const saveAndCancelButtonsJSX = (cancelDisabled, saveDisabled, handleCancelPressed, handleSavePressed, topMargin, bottomMargin, marginX, cancelColor = 'warning', saveColor = 'primary') => {
  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
      {standardButton(
        'Cancel', 
        'button',
        'contained',
        cancelDisabled,
        cancelColor, 
        topMargin,
        bottomMargin,
        marginX,
        '80px',
        '45px',
        handleCancelPressed
      )}
      {standardButton(
        saveColor === 'error' ? 'Delete' : saveColor === 'secondary' ? 'Next' : 'Save', 
        'button',
        'contained',
        saveDisabled,
        saveColor, 
        topMargin,
        bottomMargin,
        marginX,
        '80px',
        '45px',
        handleSavePressed
      )}
    </Box>
  )
}

export const doneAndCancelButtonsJSX = (doneText, cancelText, doneDisabled, cancelDisabled, handleDone, handleCancel, marginTop, marginBottom, marginX, width, doneColor = 'warning', cancelColor = 'secondary') => {
  return (
    <Box
      sx={{
        mt: width < positionChangeWidthSm ? 4 : 6, pb: 6,
        width: '90%', maxWidth: '450px',
        display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
      }}
    >
      {standardButton(
        cancelText, 
        'button',
        'contained',
        cancelDisabled,
        cancelColor, 
        marginTop,
        marginBottom,
        marginX,
        '45%', 
        '45px', 
        handleCancel
      )}
      {standardButton(
        doneText, 
        'button',
        'contained',
        doneDisabled,
        doneColor, 
        marginTop,
        marginBottom,
        marginX,
        '45%', 
        '45px', 
        handleDone
      )}
    </Box>
  )
}

export const advisoryElementJSX = (conditionText, conditionsAreMet) => {
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', 
      }}
    >
      {conditionsAreMet
        ?
        <CheckIcon fontSize="small" sx={{ border: 1, borderColor: 'blue', borderRadius: '50%', color: 'blue', height: '15px', width: '15px' }}/>
        :
        <CloseOutlinedIcon fontSize="small" sx={{ border: 1, borderColor: 'red', borderRadius: '50%', color: 'red', height: '15px', width: '15px' }}/>
      }
      <Typography
        sx={{
          ml: 1,
          color: conditionsAreMet ? 'blue' : 'red',
        }}
      >
        {conditionText}
      </Typography>
    </Box>
  )
}

// ? Vital Info Form JSX

export const ageSliderJSX = (titleText, titleSpan, sliderValue, sliderName, vitalInfoChangeHandler, setVitalInfoErrors, setVitalInfoObject, vitalInfoObject) => {
  return (
    <>
      <Typography textAlign={'left'} sx={{ width: '100%', ml: 1, mb: 1 }}>
        {titleText}: {titleSpan}
      </Typography>
      <Slider
        value={sliderValue}
        onChange={(e) => vitalInfoChangeHandler(e, setVitalInfoErrors, setVitalInfoObject, vitalInfoObject)}
        // valueLabelDisplay="on"
        name={sliderName}
        size="medium"
        min={agesArray[0]}
        max={agesArray[agesArray.length - 1]}
        // marks
        step={1}
        sx={{ 
          // px: 2,
          width: '95%', 
          align: 'center', 
        }}
      />
      <Box 
        sx={{ 
          width: '100%',
          display: 'flex', justifyContent: 'space-between', 
        }}
      >
        <Typography>
          {agesArray[0]}
        </Typography>
        <Typography>
          {agesArray[agesArray.length - 1]}
        </Typography>
      </Box>
    </>
  )
}

export const heightJSX = (vitalInfoObject, setVitalInfoObject, setVitalInfoErrors, vitalInfoChangeHandler) => {
  return (
    <Box
      sx={{ 
        mt: 2, mb: 1,
        width: '100%', 
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
      }}
    >
      <Typography textAlign={'left'} sx={{ width: '100%', ml: 1, mb: 1 }}>
        Height: {vitalInfoObject.inOrCmPreference === 'in' ? vitalInfoObject.heightInches === -1 ? <span style={{ color: 'red' }}>No height selected</span> : <span style={{ color: 'blue' }}>{inchesToFeetString(vitalInfoObject.heightInches)}</span> : vitalInfoObject.heightCM === -1 ? <span style={{ color: 'red' }}>No height selected</span> : <span style={{ color: 'blue' }}>{vitalInfoObject.heightCM} {vitalInfoObject.inOrCmPreference}</span>}
      </Typography>
      <Box
        sx={{ 
          mt: 0, mb: 0,
          width: '95%', 
          display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        {/* Height Slider */}
        <Box
          sx={{
            width: `calc(95% - 110px)`,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
          }}
        >
          <Slider
            value={vitalInfoObject.inOrCmPreference === 'in' ? vitalInfoObject.heightInches : vitalInfoObject.heightCM }
            onChange={(e) => vitalInfoChangeHandler(e, setVitalInfoErrors, setVitalInfoObject, vitalInfoObject)}
            valueLabelDisplay={vitalInfoObject.inOrCmPreference === 'in' ? 'auto' : 'off'}
            name={vitalInfoObject.inOrCmPreference === 'in' ? 'heightInches' : 'heightCM'}
            size="medium"
            min={vitalInfoObject.inOrCmPreference === 'in' ? heightInchesArray[0] : heightCMsArray[0]}
            max={vitalInfoObject.inOrCmPreference === 'in' ? heightInchesArray[heightInchesArray.length - 1] : heightCMsArray[heightCMsArray.length - 1] }
            // marks
            step={1}
            sx={{ 
              // px: 2,
              width: `100%`, 
              align: 'center', 
            }}
          />
        </Box>

        {/* Unit Preference */}
        {standardToggle(
          ['in', 'cm'], 
          'inOrCmPreference', 
          vitalInfoObject.inOrCmPreference, 
          vitalInfoChangeHandler, 
          '50px',
          setVitalInfoErrors, 
          setVitalInfoObject, 
          vitalInfoObject
        )}
      </Box>
      <Box 
        sx={{ 
          mt: -1,
          width: `100%`,
          display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', 
        }}
      >
        <Box 
          sx={{ 
            width: `calc(95% - 110px)`,
            display: 'flex', justifyContent: 'space-between', 
          }}
        >
          <Typography>
            {vitalInfoObject.inOrCmPreference === 'in' ? heightInchesArray[0] : heightCMsArray[0]}
          </Typography>
          <Typography>
            {vitalInfoObject.inOrCmPreference === 'in' ? heightInchesArray[heightInchesArray.length - 1] : heightCMsArray[heightCMsArray.length - 1]}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export const vitalInfoTextFieldJSX = (keyName, title, maxChars, marginTop, marginBottom, vitalInfoObject, setVitalInfoObject, setVitalInfoErrors, vitalInfoChangeHandler) => {
  return (
    <>
      {vitalInfoTextField(
        keyName, 
        title, 
        vitalInfoObject[keyName], 
        'outlined', 
        true, 
        true,
        false, 
        false,
        vitalInfoObject, 
        setVitalInfoObject, 
        setVitalInfoErrors, 
        vitalInfoChangeHandler, 
        maxChars,
        marginTop,
        marginBottom,
        -1
      )}
    </>
  )
}

export const vitalInfoSelectJSX = (keyName, title, array, marginTop, marginBottom, vitalInfoObject, setVitalInfoObject, setVitalInfoErrors, vitalInfoChangeHandler) => {
  return (
    <>
      {selectElement(
        keyName,
        title,
        (vitalInfoObject[keyName] === -1 || vitalInfoObject[keyName].length < 1) ? array[0] : vitalInfoObject[keyName],
        false,
        false,
        array,
        vitalInfoChangeHandler,
        vitalInfoObject,
        setVitalInfoObject,
        setVitalInfoErrors,
        marginTop,
        marginBottom,
        'white'
      )}
    </>
  )
}

export const vitalInfoSelectMultipleJSX = (name, label, disabled, value, selectOptionsArray, changeHandler, form, setForm, setErrors, marginBottom = 0, marginTop = 0, backgroundColor = 'white') => {
  return (
    <Box
      sx={{
        width: '100%',
        mt: marginTop, mb: marginBottom,
      }}
    >
      <FormControl fullWidth size='medium'>
        <InputLabel id={name}>
          {label}
        </InputLabel>
        <Select
          labelId={name}
          label={label}
          id={name}
          disabled={disabled}
          autoFocus={false}
          required={true}
          multiple
          name={name}
          value={value}
          onChange={(e) => changeHandler(e, setErrors, setForm, form)}
          sx={{
            width: '100%',
            backgroundColor: backgroundColor,
            // textAlign: 'left',
            // overflow: 'hidden',
            // textOverflow: 'ellipsis',
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} />
              ))}
            </Box>
          )}
        >
          {selectOptionsArray.map((option) => (
            <MenuItem
              key={option}
              value={option}
            >
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

// ? Media

export const displayNewMediaJSX = (newMediaArray, loadingNewMedia, handleDeleteImage, existingMediaArray = []) => {
  // console.log('existingMediaArray ->', existingMediaArray)
  return (
    <Box
      sx={{
        // backgroundColor: 'yellow',
        mt: 2,
        width: '95%', maxWidth: '500px',
        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      {existingMediaArray.length > 0 && existingMediaArray.map((existingMediaItem, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          }}
        >
          <Box 
            // key={index}
            component='img' 
            src={existingMediaItem.type === 'image' ? existingMediaItem.url : existingMediaItem.videoThumbnailURL} 
            alt='Existing Image' 
            sx={{ 
              mx: 2, mb: 0,
              height: '125px', minHeight: '125px', 
              width: '125px', minWidth: '125px', 
              objectFit: 'cover', 
              boxShadow: 2, 
            }} 
          /> 
          <Box
            sx={{
              width: '125px', maxWidth: '125px',
              display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'flex-start', 
            }}
          >
            {existingMediaItem.type === 'video' &&
              dummyBoxWidth('30px')
            }
            <Box 
              // onClick={(e) => handleDeleteImage(e, index)} 
              sx={{ 
                borderRadius: '50%', 
                display: 'flex', flexDirection: 'column', alignItems: 'center', 
              }} 
            >
              {/* <IconButton aria-label="delete-image" 
                disabled={true}
                sx={{ 
                  bottom: 22, left: 0, 
                  border: 2, borderColor: 'white', 
                  boxShadow: 3, 
                  backgroundColor: 'rgba(10,10,10,0.6)', 
                }} 
              >
                <CloseOutlinedIcon fontSize="medium" sx={{ color: 'gray' }} />
              </IconButton> */}
            </Box>
            {existingMediaItem.type === 'video' &&
              <Box
                sx={{
                  mt: -3,
                  backgroundColor: 'white',
                  opacity: 0.8,
                  fontSize: '14px',
                  width: '30px', maxWidth: '30px',
                  display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
                }}
              >
                {existingMediaItem.duration}s
              </Box>
            }
          </Box>
        </Box>
      ))}
      {newMediaArray.map((mediaFile, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          }}
        >
          <Box 
            // key={index}
            component='img' 
            src={mediaFile.imageOrVideo === 'image' ? mediaFile.squareCropDataURL : mediaFile.videoThumbnailURL} 
            alt='Image to upload' 
            sx={{ 
              mx: 2, mb: 0,
              height: '125px', minHeight: '125px', 
              width: '125px', minWidth: '125px', 
              objectFit: 'cover', 
              boxShadow: 2, 
            }} 
          /> 
          <Box
            sx={{
              width: '125px', maxWidth: '125px',
              display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'flex-start', 
            }}
          >
            {mediaFile.imageOrVideo === 'video' &&
              dummyBoxWidth('30px')
            }
            <Box 
              onClick={(e) => handleDeleteImage(e, index)} 
              sx={{ 
                borderRadius: '50%', 
                display: 'flex', flexDirection: 'column', alignItems: 'center', 
              }} 
            >
              <IconButton aria-label="delete-image" 
                disabled={loadingNewMedia}
                sx={{ 
                  bottom: 22, left: 0, 
                  border: 2, borderColor: 'white', 
                  boxShadow: 3, 
                  backgroundColor: 'rgba(10,10,10,0.6)', 
                }} 
              >
                <CloseOutlinedIcon fontSize="medium" sx={{ color: loadingNewMedia ? 'gray' : 'secondary.main' }} />
              </IconButton>
            </Box>
            {mediaFile.imageOrVideo === 'video' &&
              <Box
                sx={{
                  mt: -3,
                  backgroundColor: 'white',
                  opacity: 0.8,
                  fontSize: '14px',
                  width: '30px', maxWidth: '30px',
                  display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
                }}
              >
                {mediaFile.duration}s
              </Box>
            }
          </Box>
        </Box>
      ))} 

      {/* Dummy Boxes */}
      {(newMediaArray.length + existingMediaArray.length) > 3 && (newMediaArray.length + existingMediaArray.length) % 3 > 0 &&

        ((newMediaArray.length + existingMediaArray.length) % 3 === 1 ? [1, 2] : [1]).map( dummy => (
          dummyBoxWidth('125px', 2, dummy)
        ))
      }

    </Box>
  )

}

// ? Bio Elements (About Me, Prompts, Hobbies)

export const addBioItemIconButtonJSX = (handleAddItem, disabled) => {
  return (
    <Box 
      onClick={handleAddItem} 
      sx=
        {{ 
          borderRadius: '50%', ml: 0, mr: 0, mt: 1,
          display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', 
        }} 
    >
      <IconButton 
        aria-label="add-bio-item-text" 
        name={`addBioItem`}
        className={`add-bio-item`} 
        disabled={disabled}
        sx={{ 
          width: '30px', height: '30px', 
          mb: 1, mt: 1, mr: 2, ml: 2, bottom: 0, left: 0, 
          // border: 2, borderColor: 'white', 
          boxShadow: 3, 
        }} 
      >
        <AddIcon 
          fontSize="small" 
          color= {disabled ? '' : 'primary'}
          sx={{  }} 
        />
      </IconButton>
    </Box>
  )
}

export const deleteBioItemIconButtonJSX = (disabled, handleDeleteBioItem, width) => {
  return (
    <Box 
      onClick={(e) => disabled ? e : handleDeleteBioItem(e)} 
      sx={{ 
        ml: width < 450 ? 1 : 2,
        borderRadius: '50%', 
        display: 'flex', flexDirection: 'column', alignItems: 'center', 
      }} 
    >
      <IconButton aria-label="delete-image"
        disabled={disabled} 
        size={width < 450 ? 'small' : '' }
        sx={{ 
          // mt: -3,
          // bottom: 22, left: 0, 
          border: 2, borderColor: 'white', 
          boxShadow: 3, 
          // backgroundColor: 'rgba(10,10,10,0.6)', 
        }} 
      >
        <CloseOutlinedIcon fontSize="medium" sx={{ color: disabled ? 'gray' : 'secondary.main' }} />
      </IconButton>
    </Box>
  )
}

export const aboutMeSingleElementJSX = (aboutMeArray, changeHandlerAboutMe, setErrors, form, setForm) => {
  return (
    <Box
      sx={{
        mt: 0,
        width: '100%',
        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
      }}
    >
      {descriptionTextField(
        'aboutMe',
        aboutMeArray[0],
        0,
        `About Me ${0 + 1}`,
        5,
        300,
        'Describe yourself in a few sentences',
        true,
        changeHandlerAboutMe,
        setErrors,
        setForm,
        form,
        0
      )}
    </Box>
  )
}

export const aboutMeElementsJSX = (aboutMeArray, deleteBioItemJSX, changeHandlerAboutMe, setErrors, form, setForm ) => {
  return (
    <Box
      sx={{
        // backgroundColor: 'yellow',
        mt: 1,
        width: '100%', 
        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {aboutMeArray.length > 0 
        &&
        aboutMeArray.map((aboutMeItem, index) => (
          <Box
            key={index}
            sx={{
              mt: index > 0 ? 2 : 0,
              width: '100%',
              display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
            }}
          >
            {descriptionTextField(
              'aboutMe',
              aboutMeItem,
              index,
              `About Me ${index + 1}`,
              5,
              300,
              'Describe yourself in a few sentences',
              true,
              changeHandlerAboutMe,
              setErrors,
              setForm,
              form,
              0
            )}
            {deleteBioItemJSX(
              'aboutMe', 
              index,
              aboutMeArray.length < 2
              ||
              (
                aboutMeArray.filter(aboutMeItem => aboutMeItem.trim().length === 0).length > 0
                && aboutMeArray[index].length > 0
              )
            )}
          </Box>
        )) 
      }
    </Box>
  )
}

export const promptsElementsJSX = (promptsInBioArray, deleteBioItemJSX, changeHandlerPrompts, setErrors, form, setForm, width) => {
  return (
    <Box
      sx={{
        // backgroundColor: 'yellow',
        mt: 1,
        width: '100%',
        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {promptsInBioArray.length > 0 
        &&
        promptsInBioArray.map((promptItem, index) => (
          <Box
            key={index}
            sx={{
              mt: index > 0 ? 2 : 0,
              width: '100%',
              display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
              }}
            >
              {/* Prompt Select */}
              {selectElementWithIndex(
                'prompt',
                `Prompt ${index + 1}`,
                promptsInBioArray[index].prompt === '' ? 'Select a prompt' : promptsInBioArray[index].prompt,
                index,
                false,
                true,
                promptsInBioArray[index].prompt === '' ? [ 'Select a prompt', ...promptsArray ] : promptsArray,
                changeHandlerPrompts,
                form,
                setForm,
                setErrors,
                width,
                0,
                0,
                'white'
              )}

              {/* Response */}
              {descriptionTextField(
                'promptResponse',
                promptItem.response,
                index,
                // `Response ${index + 1}`,
                ``,
                3,
                150,
                'Your wittiest response',
                false,
                changeHandlerPrompts,
                setErrors,
                setForm,
                form,
                0
              )}
            </Box>
            {deleteBioItemJSX !== false && deleteBioItemJSX(
              'prompts', 
              index,
              promptsInBioArray.length < 4
              ||
              (
                promptsInBioArray.filter(promptItem => (promptItem.prompt.trim().length === 0 || promptItem.response.trim().length === 0)).length > 0
                && (promptsInBioArray[index].response.length > 0 && promptsInBioArray[index].prompt.length > 0 )
              )
            )}
          </Box>
        )) 
      }

    </Box>
  )
}

export const hobbiesElementsJSX = (fromElement, hobbiesArray, deleteBioItemJSX, changeHandlerHobbies, setErrors, form, setForm) => {
  return (
    <Box
      sx={{
        // backgroundColor: 'yellow',
        mt: 1,
        width: '100%', 
        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {hobbiesArray.length > 0 
        &&
        hobbiesArray.map((hobbyItem, index) => (
          <Box
            key={index}
            sx={{
              mt: index > 0 ? 2 : 0,
              width: '100%',
              display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
            }}
          >
            {vitalInfoTextField(
              'hobbies', 
              `Hobby/Interest ${index + 1}`,
              hobbyItem, 
              'outlined', 
              true, 
              true,
              false, 
              false,
              form, 
              setForm, 
              setErrors, 
              changeHandlerHobbies, 
              30,
              index === 0 ? 0 : 1,
              index === hobbiesArray.length - 1 ? 0 : 1,
              index
            )}
            {deleteBioItemJSX !== false && deleteBioItemJSX(
              'hobbies', 
              index,
              hobbiesArray.length < 6
              ||
              (
                hobbiesArray.filter(hobby => hobby.trim().length === 0).length > 0
                && hobbiesArray[index].length > 0
              )
            )}
          </Box>
        )) 
      }
    </Box>
  )
}

// ? Profiles

export const profileCardImage = (profile, videoThumbnail, profPicDefault, width, fromComponent) => {
  console.log(profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '112.5px' : '112.5px')
  return (
    ('isFriendReview' in profile && profile.isFriendReview) 
      ?
      <Box
        sx={{
          backgroundColor: 'black',
          m: 2,
          borderRadius: 4, boxShadow: 3,
          height: width < positionChangeWidthSm ? '150px' : '150px',
          minHeight: width < positionChangeWidthSm ? '150px' : '150px',
          width: width < positionChangeWidthSm ? '150px' : '150px',
          minWidth: width < positionChangeWidthSm ? '150px' : '150px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 'bold',
            color: 'white',
          }}
        >
          Friend Review
        </Typography>
      </Box>
      :
      <Box
        sx={{
          m: 2,
          width: width < positionChangeWidthSm ? '150px' : '150px',
          display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
        }}
      >
        <CardMedia
          component="img"
          height={profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '150px' : '150px' } 
          width={profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '112.5px' : '112.5px' }
          image={(!('media' in profile) && ('profilePicture' in profile)) ? profile.profilePicture : ('reviewerProfile' in profile  && profile.reviewerProfile !== null && 'profilePicture' in profile.reviewerProfile && profile.reviewerProfile.profilePicture.length > 0 && fromComponent !== 'feedbackRequest') ? profile.reviewerProfile.profilePicture : ('isFriendReview' in profile && profile.isFriendReview) ? profPicDefault : profile.media.length > 0 ? profile.media[0].mediaType === 'image' ? profile.media[0].mediaItemURL : videoThumbnail !== null ? videoThumbnail : profPicDefault : profPicDefault}
          // alt={('isFriendReview' in profile && profile.isFriendReview) ? 'Friend Review' : ''}
          sx={{ 
            // m: 2,
            borderRadius: 3,
            boxShadow: 3,
            display: 'inline-block',
            height: profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '150px' : '150px',
            width: profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '112.5px' : '112.5px',
            // minHeight: profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '150px' : '150px',
            // minWidth: profile.type === 'hinge' ? width < positionChangeWidthSm ? '150px' : '150px' : width < positionChangeWidthSm ? '112.5px' : '112.5px',
          }}
        />
      </Box>
  )
}